import React from 'react';
import { Button, Icon, Label, Segment } from 'semantic-ui-react';
import GlassmorphismTheme from '../../../styles/glassmorphism';
import { Offer, ConversationPermissions } from '../types/conversation.types';
import { ConversationService } from '../services/ConversationService';

interface OfferPreviewProps {
  offer: Offer;
  permissions: ConversationPermissions;
}

const OfferPreview: React.FC<OfferPreviewProps> = ({ offer, permissions }) => {
  // ===== FORMAT CURRENCY =====
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

  // ===== FORMAT DATA =====
  const formatData = (mb: number) => {
    if (mb >= 1024) {
      return `${(mb / 1024).toFixed(0)}GB`;
    }
    return `${mb}MB`;
  };

  // ===== HANDLE ACCEPT =====
  const handleAccept = async () => {
    try {
      await ConversationService.registerResponse(offer.clienteId, {
        tipo: 'OFERTA_ACEITA',
        conteudo: 'Aceito a oferta'
      });
      alert('Oferta aceita com sucesso!');
    } catch (err) {
      alert('Erro ao aceitar oferta');
    }
  };

  // ===== HANDLE REJECT =====
  const handleReject = async () => {
    try {
      await ConversationService.registerResponse(offer.clienteId, {
        tipo: 'OFERTA_RECUSADA',
        conteudo: 'Recuso a oferta'
      });
      alert('Oferta recusada');
    } catch (err) {
      alert('Erro ao recusar oferta');
    }
  };

  // ===== HANDLE ESCALATE =====
  const handleEscalate = async () => {
    try {
      if (permissions.canEscalate) {
        const motivo = prompt('Motivo da escalação:') || 'Escalação manual pelo usuário';
        await ConversationService.escalateToHuman(offer.clienteId, motivo);
        alert('Cliente escalado para atendente');
      }
    } catch (err) {
      alert('Erro ao escalar atendente');
    }
  };

  return (
    <Segment
      style={{
        ...GlassmorphismTheme.components.glassCard,
        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15), rgba(74, 144, 226, 0.1))',
        border: GlassmorphismTheme.borders.glassAccent,
      }}
    >
      <h4 style={{ marginTop: 0, color: '#667eea' }}>
        <Icon name="gift" /> Oferta Especial
      </h4>

      {/* OFFERS GRID */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: GlassmorphismTheme.spacing.md,
        marginBottom: GlassmorphismTheme.spacing.lg,
      }}>
        {offer.ofertas.map((oferta, idx) => (
          <div key={idx} style={{
            padding: GlassmorphismTheme.spacing.md,
            background: 'rgba(255, 255, 255, 0.3)',
            borderRadius: GlassmorphismTheme.borderRadius.md,
            border: `1px solid rgba(255, 255, 255, 0.5)`,
          }}>
            <small style={{ color: '#667eea', fontWeight: 600 }}>Linha: {oferta.numeroLinha}</small>

            {/* CURRENT PLAN */}
            <div style={{ marginTop: GlassmorphismTheme.spacing.sm }}>
              <small style={{ color: '#95a5a6' }}>Plano Atual</small>
              <p style={{ margin: '4px 0 0 0', fontWeight: 600, fontSize: '0.9rem' }}>
                {oferta.planoAtual}
              </p>
              <small style={{ color: '#7f8c8d' }}>
                {formatData(oferta.dataAtualMb)} • {formatCurrency(oferta.valorAtual)}/mês
              </small>
            </div>

            {/* ARROW */}
            <div style={{ margin: `${GlassmorphismTheme.spacing.sm} 0`, textAlign: 'center' }}>
              <Icon name="arrow down" style={{ color: '#27ae60' }} />
            </div>

            {/* NEW PLAN */}
            <div>
              <small style={{ color: '#27ae60', fontWeight: 600 }}>Novo Plano</small>
              <p style={{ margin: '4px 0 0 0', fontWeight: 600, fontSize: '0.9rem' }}>
                {oferta.planoOfertado}
              </p>
              <small style={{ color: '#7f8c8d' }}>
                {formatData(oferta.dataOfertadoMb)} • {formatCurrency(oferta.valorOfertado)}/mês
              </small>
            </div>

            {/* SAVINGS */}
            <div style={{
              marginTop: GlassmorphismTheme.spacing.md,
              padding: `${GlassmorphismTheme.spacing.sm} ${GlassmorphismTheme.spacing.md}`,
              background: 'rgba(39, 174, 96, 0.1)',
              borderRadius: GlassmorphismTheme.borderRadius.sm,
              textAlign: 'center',
            }}>
              <small style={{ color: '#7f8c8d' }}>Economia Mensal</small>
              <p style={{ margin: '4px 0 0 0', fontWeight: 700, color: '#27ae60', fontSize: '1.1rem' }}>
                {formatCurrency(oferta.economia)}
              </p>
            </div>

            {/* BENEFITS */}
            {oferta.beneficios.length > 0 && (
              <div style={{ marginTop: GlassmorphismTheme.spacing.md }}>
                <small style={{ color: '#667eea', fontWeight: 600 }}>Benefícios</small>
                <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px', fontSize: '0.85rem' }}>
                  {oferta.beneficios.map((benefit, i) => (
                    <li key={i} style={{ color: '#2c3e50', marginBottom: '2px' }}>{benefit}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* HIDDEN ADDON */}
            {oferta.aditivoEncoberto && (
              <Label
                style={{
                  marginTop: GlassmorphismTheme.spacing.md,
                  background: 'rgba(243, 156, 18, 0.2)',
                  color: '#f39c12',
                  border: `1px solid #f39c12`,
                }}
              >
                <Icon name="lock" /> Aditivo: {formatCurrency(oferta.aditivoEncoberto.valor)}
              </Label>
            )}
          </div>
        ))}
      </div>

      {/* SUMMARY */}
      <div style={{
        padding: GlassmorphismTheme.spacing.md,
        background: 'rgba(255, 255, 255, 0.3)',
        borderRadius: GlassmorphismTheme.borderRadius.md,
        marginBottom: GlassmorphismTheme.spacing.lg,
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: GlassmorphismTheme.spacing.md }}>
          <div>
            <small style={{ color: '#95a5a6' }}>Total Mensal Atual</small>
            <p style={{ margin: '4px 0 0 0', fontWeight: 600, fontSize: '1rem' }}>
              {formatCurrency(offer.totalMensalAtual)}
            </p>
          </div>
          <div>
            <small style={{ color: '#27ae60', fontWeight: 600 }}>Total Mensal Novo</small>
            <p style={{ margin: '4px 0 0 0', fontWeight: 600, fontSize: '1rem' }}>
              {formatCurrency(offer.totalMensalNovo)}
            </p>
          </div>
        </div>
      </div>

      {/* ACTIONS */}
      <div style={{
        display: 'flex',
        gap: GlassmorphismTheme.spacing.sm,
        justifyContent: 'flex-end',
      }}>
        {permissions.canEscalate && (
          <Button
            basic
            icon
            labelPosition="left"
            onClick={handleEscalate}
            style={{ ...GlassmorphismTheme.components.buttonGlass }}
          >
            <Icon name="phone" /> Atendente
          </Button>
        )}
        <Button
          basic
          onClick={handleReject}
          style={{ ...GlassmorphismTheme.components.buttonGlass }}
        >
          <Icon name="close" /> Recusar
        </Button>
        <Button
          onClick={handleAccept}
          style={{ ...GlassmorphismTheme.components.buttonPrimary }}
        >
          <Icon name="check" /> Aceitar
        </Button>
      </div>

      {/* VALIDITY */}
      <small style={{
        display: 'block',
        marginTop: GlassmorphismTheme.spacing.md,
        textAlign: 'center',
        color: '#95a5a6',
      }}>
        Válida até {new Date(offer.dataValidade).toLocaleDateString('pt-BR')}
      </small>
    </Segment>
  );
};

export default OfferPreview;
